import React, {
    useRef,
    useEffect,
} from 'react';
import {
    useSsSelector as useSelector,
    useSsDispatch as useDispatch,
} from '~SpectateStore';
import { useTranslation } from 'react-i18next';

import {
    addUserMultiple,
    editUserMultiple,
    saveFormattedValue,
    toggleEditor,
    removeMultiple,
} from '~Betslip/slices/userMultiplesSlice';

import {
    selectCountUserMultiples,
    selectMultipleBeingEdited,
    selectAvailableMultiples,
} from '~Betslip/selectors/userMultiplesSelectors';

import {
    selectAvailableFreebets,
} from '~Betslip/selectors/freeBetSelectors';

import {
    useKeyboard,
} from '~Betslip/store/keyboardContext';

import {
    useIsEachwayAllowed,
} from '~SpectateStore/hooks/userHooks';

import {
    selectHasMultiplesExceedBalance,
} from '~Betslip/selectors/userSelectors';

import {
    selectIsRefreshingBetslipData,
} from '~Betslip/selectors/uiSelectors';

import StakeField from '../StakeField';

const MultipleEditor = () => {
    const dispatch = useDispatch();
    const userMultiplesCount = useSelector(selectCountUserMultiples);

    const availableMultiples = useSelector(selectAvailableMultiples);
    const currentMultiple = useSelector(selectMultipleBeingEdited);

    const { isDepositNeeded } = useSelector(selectHasMultiplesExceedBalance);

    const isEachwayAllowed = useIsEachwayAllowed();
    const isRefreshingBetslipData = useSelector(selectIsRefreshingBetslipData);

    useEffect(() => {
        if (userMultiplesCount === 0) {
            // If no userMultiples, we add the first-one
            const [{ id: betTypeId }] = availableMultiples;
            dispatch(addUserMultiple(betTypeId));
        }
    }, [availableMultiples]);

    const [ t ] = useTranslation();

    const keyboardRef = useRef();
    const { setKeyboardContainerRef, setFreebets } = useKeyboard();

    let betslipId = null;

    if (currentMultiple.free_bet_available) {
        betslipId = currentMultiple.id;
    }

    const freebetId = currentMultiple?.free_bet_id;
    const freebets = useSelector((state) => selectAvailableFreebets(state, betslipId, freebetId));
    setFreebets(freebets);

    const renderAvailableMultiples = availableMultiples.map(betType => (
        <option
            key={betType.id}
            value={betType.id}
            dir="auto"
        >
            {t(`bet_types.name.${betType.name}`)} ({t('betslip.bets_number', { count: betType.total })})
        </option>
    ));

    const onChangeEditor = (stake, each_way_selected, free_bet_id) => {
        const fbStake = (free_bet_id) ? 0 : stake;
        dispatch(editUserMultiple({
            id: currentMultiple.id,
            stake: fbStake,
            each_way_selected,
            free_bet_id,
        }));
    };

    const openKeyboard = () => {
        setKeyboardContainerRef(keyboardRef.current);
    };

    const onAcceptHandler = () => {
        dispatch(toggleEditor());
        dispatch(saveFormattedValue(currentMultiple.id));
    };

    const onChangeBetTypeSelect = (e) => {
        const betTypeId = e.target.value;
        dispatch(removeMultiple(currentMultiple.id));
        dispatch(addUserMultiple(betTypeId));
    };

    const gtmDetails = {
        source: 'new-betslip',
        betType: currentMultiple.id,
    };

    return (
        <div className="fullslip__multiple-editor">
            <div className="fullslip__multiple-editor-dropdown">
                <select
                    className="fullslip__multiple-editor-select"
                    name="multiples"
                    value={currentMultiple.id}
                    onChange={onChangeBetTypeSelect}
                    dir="auto"
                >
                    {renderAvailableMultiples}
                </select>
            </div>


            <div className="fullslip__multiple-editor-stake">
                <div className="fullslip__multiple-editor-wager">
                    {t('betslip.wager_per_bet')}
                    <span className="fullslip__multiple-editor-bets-counter" dir="auto">
                        ({t('betslip.bets_number', { count: currentMultiple?.total })})
                    </span>
                </div>
                <StakeField
                    stake={currentMultiple?.stake}
                    each_way_allowed={currentMultiple?.each_way_allowed && isEachwayAllowed}
                    each_way_selected={currentMultiple?.each_way_selected}
                    onChangeCallback={onChangeEditor}
                    onClickHandler={openKeyboard}
                    onAcceptHandler={onAcceptHandler}
                    freebetId={freebetId}
                    betslipId={betslipId}
                    gtmDetails={gtmDetails}
                    depositRequired={isDepositNeeded}
                    disabled={isRefreshingBetslipData}
                />
            </div>
            <div ref={keyboardRef} />
        </div>
    );
};

export default MultipleEditor;
